import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Tariff } from "../../../types/tariff";
import { createGroup } from "../../../services/groups";
import { STATIC_TEMPLATE } from "../../../utils/tariffBuilderUtils";
import MembersInput from "../GroupEdit/components/MembersInput";
import StationsMultiSelect from "../GroupEdit/components/StationsMultiSelectOps";
import TariffEditor from "../GroupEdit/components/TariffEditor";

import "../GroupEdit/GroupEdit.css";

const GroupCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [name, setName] = useState("");
  const [stationIds, setStationIds] = useState<string[]>([]);
  const [members, setMembers] = useState<string[]>([]);
  const [tariff, setTariff] = useState<Tariff>({ ...(STATIC_TEMPLATE as unknown as Tariff), last_updated: new Date().toISOString() });
  const [saving, setSaving] = useState(false);
  const [isTariffValid, setIsTariffValid] = useState(true);

  const handleAddUser = useCallback((email: string) => {
    setMembers((prev) => [...prev, email]);
  }, []);

  const handleRemoveUser = useCallback((email: string) => {
    setMembers((prev) => prev.filter((m) => m !== email));
  }, []);

  const handleCreate = async () => {
    (document.activeElement as HTMLElement)?.blur();
    await new Promise((r) => setTimeout(r, 0));
    if (!name.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Validation",
        detail: "Group name is required.",
        life: 3000,
      });
      return;
    }

    setSaving(true);
    try {
      const created = await createGroup({
        name: name.trim(),
        stationIds,
        memberIdentifiers: members.map((email) => ({ email })),
        tariff,
      });
      toast.current?.show({
        severity: "success",
        summary: "Created",
        detail: "Group created successfully.",
        life: 2000,
      });
      setTimeout(() => navigate(`/target-groups/${created.groupId}`), 1500);
    } catch (err: any) {
      const data = err?.response?.data;
      let detail = "Failed to create group.";
      if (data?.error_code === "member_is_station_owner_or_manager") {
        const { email, station_id } = data.error_args ?? {};
        detail = `${email} is already an owner or manager on station ${station_id} and cannot be added to this group.`;
      }
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail,
        life: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Toast
        ref={toast}
        position="top-right"
        appendTo={typeof window !== "undefined" ? document.body : undefined}
      />

      <div className="group-edit-hero">
        <div className="flex justify-content-between align-items-center">
          <div>
            <h1 className="group-edit-hero-title">New Group</h1>
            <div className="group-edit-hero-sub">
              Fill in the details below to create a target group
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              outlined
              style={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)" }}
              onClick={() => navigate("/target-groups")}
            />
            <Button
              label="Create group"
              icon="pi pi-check"
              loading={saving}
              disabled={!isTariffValid}
              style={{
                background: "#c3e76b",
                color: "#1a3d35",
                border: "none",
                fontWeight: 700,
              }}
              onClick={handleCreate}
            />
          </div>
        </div>
      </div>

      <div className="group-edit-grid">
        <div className="group-edit-panel group-edit-panel--full">
          <p className="group-edit-section-title">Group Name</p>
          <InputText
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter group name"
            className="w-full"
          />
        </div>

        <div className="group-edit-panel">
          <p className="group-edit-section-title">Stations</p>
          <StationsMultiSelect
            value={stationIds}
            onChange={setStationIds}
          />
        </div>

        <div className="group-edit-panel">
          <p className="group-edit-section-title">Members</p>
          <MembersInput
            members={members}
            stationIds={stationIds}
            onAdd={handleAddUser}
            onRemove={handleRemoveUser}
          />
        </div>

        <div className="group-edit-panel group-edit-panel--full">
          <p className="group-edit-section-title">
            <i className="pi pi-tag" style={{ marginRight: "0.4rem" }} />
            Tariff
          </p>
          <TariffEditor tariff={tariff} onChange={setTariff} onValidChange={setIsTariffValid} />
        </div>
      </div>
    </>
  );
};

export default GroupCreate;
